import React from 'react'
import { MapStateToProps, connect } from 'react-redux'
import { State } from '../../../../../../redux/State'
import { Beta } from '../../../../../../redux/State/Beta'
import { Client } from '../../../../../../redux/State/Client'
import { SyncPromise } from '../../../../../../redux/SyncPromise'
import { I18n, getI18n } from './ProfileCard.i18n'
import { User } from '../../../../../../redux/State/User'
import { ClientProfile } from '../../../../../../redux/State/Client/Profile'
import { DocumentUpload } from '../../../../../../redux/State/Client/IClient'
import { Config } from '../../../../../../redux/State/Config'

interface StateProps {
  i18n: I18n
  name: SyncPromise<string>
  beta: boolean
  user: SyncPromise<User>
  profile?: SyncPromise<ClientProfile>
  priceAdjustment: SyncPromise<{
    discount: number
    type: string
  }>
  callToActionMessage: string[] | undefined
  shopifyUrl: string
}

export type VacCoverageView = 'loading' | 'nocoverage' | 'coverage'
export type VacDocumentStatus = 'none' | 'docreceived'

/**
 * When false, the doc-received status, the submission instructions/notice,
 * and the VAC uploader link/button are all hidden. Only the base
 * no-coverage message (nocoverage view) or the title/remaining
 * coverage/period (coverage view) are shown.
 */
const SHOW_VAC_UPLOADER_AND_DOC_STATUS = false

export function getLatestVacDocument (
  documentUploads: DocumentUpload[] | undefined,
): DocumentUpload | undefined {
  let latest: DocumentUpload | undefined
  let latestTime = -Infinity

  for (const doc of documentUploads ?? []) {
    if (doc.documentType !== 'MBC Authorization Form') continue
    const t = new Date(doc.createdOn).getTime()
    if (t > latestTime) {
      latestTime = t
      latest = doc
    }
  }

  return latest
}

/**
 * Formats an ISO createdOn string into "Month Day, Year" (e.g. "May 12, 2026").
 */
export function formatDocumentDate (createdOn: string | undefined): string {
  if (!createdOn) return ''
  const date = new Date(createdOn)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * default view = "nocoverage"
 * if policyType is "Pre-VAC" or "VAC" -> "coverage"
 * else -> "nocoverage"
 */
export function getVacCoverageView (
  policyType: string | undefined,
): Exclude<VacCoverageView, 'loading'> {
  if (policyType === 'Pre-VAC' || policyType === 'VAC') {
    return 'coverage'
  }

  return 'nocoverage'
}

/**
 * if uploadStatus == "New" -> "docreceived", independently of coverage status.
 */
export function getVacDocumentStatus (
  uploadStatus: string | undefined,
): VacDocumentStatus {
  return uploadStatus === 'New' ? 'docreceived' : 'none'
}

/**
 * Whether a current coverage period is set at all (either boundary present).
 * Coverage itself doesn't require a period - this is only used to decide
 * whether to show the period dates and the "- Current Period" title suffix.
 */
export function hasCurrentPeriod (
  startDate: string | undefined,
  endDate: string | undefined,
): boolean {
  return !!startDate || !!endDate
}

/**
 * check if today is within [startDate, endDate], treating a missing
 * boundary as open-ended on that side.
 */
export function isWithinCurrentPeriod (
  startDate: string | undefined,
  endDate: string | undefined,
): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (startDate) {
    const start = new Date(startDate)
    start.setHours(0, 0, 0, 0)
    if (today < start) return false
  }

  if (endDate) {
    const end = new Date(endDate)
    end.setHours(0, 0, 0, 0)
    if (today > end) return false
  }

  return true
}

export const VacCoverageCardPres: React.FC<StateProps> = ({
  i18n,
  user,
  shopifyUrl,
}) => {
  const latestVacDocument = getLatestVacDocument(user.value?.client?.documentUploads)
  const policyType = user.value?.client?.currentCoverage?.policyType
  const uploadStatus = latestVacDocument?.statusCode
  const knumber = user.value?.client?.profile?.insurance?.vac?.kNumber

  // user.value is undefined until the SyncPromise resolves; treat that as "still loading"
  if (!user.value) {
    return (
      <div style={{ display: 'flex-column' }}>
        <h2>{ i18n.VAC_COVERAGE_TITLE }</h2>
        <div id="vac_loading" aria-busy="true">
          <div>
            <p className="paleblue-highlight" style={{ padding: '10px', borderRadius: '8px' }}>Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!knumber) {
    return null
  }

  const view: Exclude<VacCoverageView, 'loading'> = getVacCoverageView(policyType)
  const documentStatus: VacDocumentStatus = getVacDocumentStatus(uploadStatus)

  const periodStart = user.value?.client?.profile?.insurance?.period?.startDate
  const periodEnd = user.value?.client?.profile?.insurance?.period?.endDate
  const showCurrentPeriod = hasCurrentPeriod(periodStart, periodEnd)
  const isActivePeriod = showCurrentPeriod && isWithinCurrentPeriod(periodStart, periodEnd)

  return (
    <div style={{ display: 'flex-column' }}>
      <h2>{ i18n.VAC_COVERAGE_TITLE }</h2>

      {view === 'nocoverage' && (
        <div id="vac_nocoverage">
          <div className="paleblue-highlight">
            <p style={{ fontWeight: 700 }}>{ i18n.VAC_COVERAGE_NO_COVERAGE }</p>
          </div>

          { SHOW_VAC_UPLOADER_AND_DOC_STATUS && (
            documentStatus === 'docreceived' ? (
              <div id="vac_docreceived">
                <div className="paleblue-highlight">
                  <p style={{ fontWeight: 700 }}>{ i18n.VAC_DOCUMENT_RECEIVED(formatDocumentDate(latestVacDocument?.createdOn)) }</p>
                  <p>{ i18n.VAC_UNDER_REVIEW }</p>
                </div>
              </div>
            ) : (
              <>
                <div className="paleblue-highlight">
                  <p>{ i18n.VAC_COVERAGE_SUBMIT_INSTRUCTIONS }</p>
                  <a href={`${shopifyUrl}/pages/vac-document-uploader`} rel="noopener noreferrer" target="_blank" type="button" className="ant-btn ant-btn-round">
                    <span>{ i18n.VAC_COVERAGE_SUBMIT_BUTTON }</span>
                  </a>
                </div>
                <p>
                  { i18n.VAC_COVERAGE_ALREADY_SENT_NOTICE }
                </p>
              </>
            )
          ) }
        </div>
      )}

      {view === 'coverage' && (
        <div id="vac_coverage">
          <div>
            <p style={{ fontWeight: 600 }}>
              { policyType === 'Pre-VAC' ? i18n.VAC_PRE_COVERAGE_TITLE : i18n.VAC_COVERAGE_PLAN_TITLE }
              { isActivePeriod && ` - ${i18n.VAC_CURRENT_PERIOD_SUFFIX}` }
            </p>
            <p style={{ marginBottom: '0' }}>{ i18n.VAC_REMAINING_COVERAGE(user.value?.client?.profile?.insurance?.period?.allowanceRemaining ?? 0) }</p>
            { showCurrentPeriod && <p>{ i18n.VAC_CURRENT_PERIOD_SPAN(periodStart ?? '', periodEnd ?? '') }</p> }
          </div>

          { SHOW_VAC_UPLOADER_AND_DOC_STATUS && (
            documentStatus === 'docreceived' ? (
              <div id="vac_docreceived">
                <div className="paleblue-highlight">
                  <p style={{ fontWeight: 700 }}>{ i18n.VAC_DOCUMENT_RECEIVED(formatDocumentDate(latestVacDocument?.createdOn)) }</p>
                  <p>{ i18n.VAC_UNDER_REVIEW }</p>
                </div>
              </div>
            ) : (
              <>
                <div className="paleblue-highlight">
                  <p>{ policyType === 'Pre-VAC' ? i18n.VAC_ACTIVATE_INSTRUCTIONS : i18n.VAC_COVERAGE_SUBMIT_INSTRUCTIONS }</p>
                  <a href={`${shopifyUrl}/pages/vac-document-uploader`} rel="noopener noreferrer" target="_blank" type="button" className="ant-btn ant-btn-round">
                    <span>{ i18n.VAC_COVERAGE_SUBMIT_BUTTON }</span>
                  </a>
                </div>
                <p>
                  { i18n.VAC_COVERAGE_ALREADY_SENT_NOTICE }
                </p>
              </>
            )
          ) }
        </div>
      )}
    </div>
  )
}

const mapStateToProps: MapStateToProps<StateProps, {}, State> = (
  state,
  props,
): StateProps => {
  return {
    i18n: getI18n(state.language),
    name: Client.getName(),
    beta: Beta.is(),
    user: User.get(),
    profile: ClientProfile.get(),
    priceAdjustment: Client.get().map(c => c.priceAdjustment),
    callToActionMessage: Client.getCallToActionMessage(),
    shopifyUrl: Config.get().value?.URL_SHOPIFY ?? '',
  }
}

export const VacCoverageCard = connect(mapStateToProps)(VacCoverageCardPres)
